import { API_METHODS } from '@compass/client/constants/api-methods';
import type { Team } from '@compass/client/types/team';
import { Edit2, MoreVertical, Plus, Search, Trash2, Users as UsersIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { PermissionGuard } from '@/components/PermissionGuard';
import { CreateTeamDialog } from '@/components/settings/CreateTeamDialog';
import { DeleteTeamDialog } from '@/components/settings/DeleteTeamDialog';
import { EditTeamDialog } from '@/components/settings/EditTeamDialog';
import { ManageTeamMembersDialog } from '@/components/settings/ManageTeamMembersDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserCell } from '@/components/UserCell';
import { useOrganizationTeams, useTeamMembersMap } from '@/hooks/useTeams';
import { useStore } from '@/store';

interface OrganizationTeamsProps {
  organizationId: string;
}

export const OrganizationTeams: React.FC<OrganizationTeamsProps> = ({ organizationId }) => {
  const { programs } = useStore();

  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [manageMembersTeam, setManageMembersTeam] = useState<Team | null>(null);
  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [deleteTeam, setDeleteTeam] = useState<Team | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { teams: allTeams, isLoading, error } = useOrganizationTeams(organizationId);
  const { usersMap } = useTeamMembersMap(allTeams);

  // Build a map of teamId -> program names (for the Program column).
  const teamProgramMap = useMemo(() => {
    const nameMap = new Map<string, string[]>();
    for (const program of Object.values(programs)) {
      if (program.teams) {
        for (const teamId of program.teams) {
          const names = nameMap.get(teamId) || [];
          names.push(program.name || 'Unnamed Program');
          nameMap.set(teamId, names);
        }
      }
    }
    return nameMap;
  }, [programs]);

  const teams = useMemo(() => {
    if (!searchQuery.trim()) return allTeams;
    const query = searchQuery.toLowerCase().trim();
    return allTeams.filter(
      t => t.name?.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query),
    );
  }, [allTeams, searchQuery]);

  const hasError = !!error;
  const allTeamsCount = allTeams.length;
  const isEmpty = !isLoading && teams.length === 0;
  const isSearchEmpty = isEmpty && searchQuery.trim() && allTeamsCount > 0;

  return (
    <div className="rounded-lg border bg-card p-6">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Teams</h2>
          <p className="text-sm text-muted-foreground">Manage teams across the organization.</p>
        </div>
        <PermissionGuard apiMethod={[API_METHODS.OrganizationTeamService.CreateOrganizationTeam]}>
          <Button size="sm" onClick={() => setCreateTeamDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Team
          </Button>
        </PermissionGuard>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search teams..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9"
        />
      </div>

      {/* Content */}
      {hasError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-destructive">Failed to load teams</p>
          <p className="mt-2 text-xs text-muted-foreground">{(error as Error).message}</p>
        </div>
      ) : isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-64" />
              </div>
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <div className="py-8 text-center text-muted-foreground">
          <UsersIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-4">
            {isSearchEmpty
              ? `No teams found matching "${searchQuery}"`
              : 'No teams in this organization'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <div className="max-h-[340px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 border-b bg-card">
                <TableRow>
                  <TableHead>Team Name</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Team Lead</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teams.map(team => {
                  const manager = team.manager_id ? usersMap.get(team.manager_id) : null;
                  const memberCount = team.member_ids?.length || 0;
                  const programNames = team.id ? teamProgramMap.get(team.id) : undefined;
                  const programName = programNames?.join(', ');

                  return (
                    <TableRow key={team.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{team.name || 'Unnamed Team'}</span>
                          <p className="text-sm text-muted-foreground">
                            {memberCount} {memberCount === 1 ? 'member' : 'members'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {programName || '\u2014'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {team.manager_id ? (
                          <UserCell user={manager ?? undefined} fallbackId={team.manager_id} />
                        ) : (
                          <span className="text-sm text-muted-foreground">{'\u2014'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setManageMembersTeam(team)}>
                              <UsersIcon className="mr-2 h-4 w-4" />
                              Manage Members
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditTeam(team)}>
                              <Edit2 className="mr-2 h-4 w-4" />
                              Edit Team
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteTeam(team)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <CreateTeamDialog
        organizationId={organizationId}
        open={createTeamDialogOpen}
        onOpenChange={setCreateTeamDialogOpen}
      />
      <EditTeamDialog
        organizationId={organizationId}
        team={editTeam}
        open={!!editTeam}
        onOpenChange={open => !open && setEditTeam(null)}
      />
      <ManageTeamMembersDialog
        organizationId={organizationId}
        team={manageMembersTeam}
        open={!!manageMembersTeam}
        onOpenChange={open => !open && setManageMembersTeam(null)}
      />
      <DeleteTeamDialog
        organizationId={organizationId}
        team={deleteTeam}
        open={!!deleteTeam}
        onOpenChange={open => !open && setDeleteTeam(null)}
      />
    </div>
  );
};
